// Core Imports
import React, { useState, useEffect, useRef } from "react"
import {
  Fab,
  Box,
  TextField,
  MenuItem,
  Icon,
  IconButton,
  InputAdornment,
  Grid,
  Typography,
  Button,
} from "@material-ui/core"
import { useSnackbar } from "notistack"
import LAMP from "lamp-core"
import locale_lang from "../locale_map.json"
import { Service } from "./DBService/DBService"

// Local Imports
import { useTranslation } from "react-i18next"
import { clearLocalStorageItems } from "./helper"
import { useAuthContext } from "./AuthProvider"
import LoginFrame, { useLoginStyles } from "./LoginFrame"
import { KNOWN_SERVERS, defaultServerAddress } from "./ServerGateway"
import { beginSignIn, signInRefusalMessage } from "../signInStatus"
import { MfaError, mfaErrorMessage, mfaSignIn, isInNativeApp } from "../mfaSession"
import MfaCodeStep, { MfaCodeRequest } from "./MfaCodeStep"

export default function Login({ setIdentity, lastDomain, onComplete, setConfirmSession, clearServer, ...props }) {
  const { t, i18n } = useTranslation()
  const [state, setState] = useState({ id: undefined, password: undefined })
  const [showPassword, setShowPassword] = useState(false)
  const [loginClick, setLoginClick] = useState(true)
  const { enqueueSnackbar } = useSnackbar()
  const classes = useLoginStyles()
  // The code step of administrators and researchers (mfaSession.ts), and the sign-in waiting on it.
  const [mfaRequest, setMfaRequest] = useState<MfaCodeRequest | null>(null)
  // Shown in place of a snackbar when an administrator or researcher tries the app.
  const [appOnlyParticipants, setAppOnlyParticipants] = useState(false)
  const mfaDone = useRef<(verified: boolean) => void>()
  const askForCode = (request: MfaCodeRequest) =>
    new Promise<boolean>((resolve) => {
      mfaDone.current = resolve
      setMfaRequest(request)
    })
  const userLanguages = ["en-US", "ko-KR"]
  const getSelectedLanguage = () => {
    const matched_codes = Object.keys(locale_lang).filter((code) => code.startsWith(navigator.language))
    const lang = matched_codes.length > 0 ? matched_codes[0] : "en-US"
    return i18n.language ? i18n.language : userLanguages.includes(lang) ? lang : "en-US"
  }
  const [selectedLanguage, setSelectedLanguage]: any = useState(getSelectedLanguage())
  const MAX_ATTEMPTS = 5
  const LOCKOUT_DURATION = 60 * 60 * 1000
  const LOGIN_ATTEMPTS_KEY = "loginAttempts"
  const LOCKOUT_TIME_KEY = "lockoutTime"
  const [isLockedOut, setIsLockedOut] = useState(false)
  const { setIsLoggedIn } = useAuthContext()
  const srcLocked = props.srcLocked

  useEffect(() => {
    setConfirmSession(false)
    let lockoutTime = null
    if (typeof localStorage.getItem(LOCKOUT_TIME_KEY) != "undefined") {
      lockoutTime = localStorage.getItem(LOCKOUT_TIME_KEY)
    }
    if (lockoutTime) {
      // The stored time is already the end of the lockout (Date.now() + LOCKOUT_DURATION when it began),
      // never more than LOCKOUT_DURATION away (a clock that was ahead when it began).
      const lockoutEnd = Math.min(parseInt(lockoutTime), Date.now() + LOCKOUT_DURATION)
      const now = Date.now()
      if (now < lockoutEnd) {
        setIsLockedOut(true)
        scheduleUnlock(lockoutEnd)
      } else {
        // Lockout expired
        localStorage.removeItem(LOCKOUT_TIME_KEY)
        localStorage.removeItem(LOGIN_ATTEMPTS_KEY)
      }
    }
    checkMAxAttempts()
  }, [])

  useEffect(() => {
    i18n.changeLanguage(selectedLanguage)
  }, [selectedLanguage])

  let handleChange = (event) =>
    setState({
      ...state,
      [event.target.name]: event.target.type === "checkbox" ? event.target.checked : event.target.value,
    })

  const scheduleUnlock = (lockoutEnd: number) =>
    setTimeout(() => {
      setIsLockedOut(false)
      localStorage.removeItem(LOCKOUT_TIME_KEY)
      localStorage.removeItem(LOGIN_ATTEMPTS_KEY)
    }, Math.max(0, lockoutEnd - Date.now()))

  const checkMAxAttempts = () => {
    const attempts = parseInt(localStorage.getItem(LOGIN_ATTEMPTS_KEY) || "0")
    if (attempts >= MAX_ATTEMPTS) {
      const lockoutEnd = Math.min(
        parseInt(localStorage.getItem(LOCKOUT_TIME_KEY) || "0"),
        Date.now() + LOCKOUT_DURATION
      )
      if (lockoutEnd && Date.now() >= lockoutEnd) {
        // The lockout is over (the page stayed open through it).
        localStorage.removeItem(LOCKOUT_TIME_KEY)
        localStorage.removeItem(LOGIN_ATTEMPTS_KEY)
        setIsLockedOut(false)
        return true
      }
      // Keep the end of a lockout already under way: restarting it on every visit or click would
      // make it last longer than the hour the message says.
      if (!lockoutEnd) {
        const lockoutUntil = Date.now() + LOCKOUT_DURATION
        localStorage.setItem(LOCKOUT_TIME_KEY, lockoutUntil.toString())
      }
      setIsLockedOut(true)
      return false
    }
    return true
  }

  let handleLogin = async (event: any) => {
    event.preventDefault()
    clearLocalStorageItems()
    if (!checkMAxAttempts()) {
      return
    }
    const attempts = parseInt(localStorage.getItem(LOGIN_ATTEMPTS_KEY) || "0")
    setLoginClick(true)

    // setLoginClick(true)
    if (!state.id || !state.password) {
      enqueueSnackbar(`${t("Incorrect username, password, or server address.")}`, {
        variant: "error",
      })
      return
    }

    let res: any
    {
      try {
        beginSignIn()
        // Administrators and researchers also enter a code emailed to them; this step checks the
        // password too. An older server without it answers "unsupported" and nothing changes.
        const serverAddress =
          LAMP.Auth._auth.serverAddress || localStorage.getItem("lastServerSelected") || defaultServerAddress()
        const inApp = isInNativeApp()
        const step = await mfaSignIn(serverAddress, state.id, state.password, { sendCode: !inApp })
        // The app is for participants: its WebView would get around the code, and their data is not there.
        if (inApp && !!step.type && step.type !== "participant") {
          setAppOnlyParticipants(true)
          setLoginClick(false)
          return
        }
        if (step.mfa === "code-sent") {
          const verified = await askForCode({
            serverAddress,
            id: state.id,
            password: state.password,
            emailHint: step.email_hint,
            expiresIn: step.expires_in,
          })
          if (!verified) {
            setLoginClick(false)
            return
          }
        }
        res = await setIdentity({
          id: state.id,
          password: state.password,
          serverAddress: LAMP.Auth._auth.serverAddress,
        })
      } catch (err) {
        // The code could not be sent and the like: say so, and don't count it as a wrong password.
        const mfaMessage = err instanceof MfaError ? mfaErrorMessage(err.message, t) : null
        if (mfaMessage) {
          enqueueSnackbar(mfaMessage, { variant: "error" })
          setLoginClick(false)
          return
        }
        // The server is throttling sign-ins to this account, or the account was deleted: say so,
        // and don't count it as a failed attempt here.
        const refusalMessage = signInRefusalMessage(t)
        if (refusalMessage) {
          enqueueSnackbar(refusalMessage, { variant: "error" })
          setLoginClick(false)
          return
        }
        // Failed login throws an error
        const currentAttempts = attempts + 1
        localStorage.setItem(LOGIN_ATTEMPTS_KEY, currentAttempts.toString())
        if (currentAttempts >= MAX_ATTEMPTS) {
          const lockoutUntil = Date.now() + LOCKOUT_DURATION
          localStorage.setItem(LOCKOUT_TIME_KEY, lockoutUntil.toString())
          setIsLockedOut(true)
          scheduleUnlock(lockoutUntil)
          setLoginClick(false)
        } else {
          enqueueSnackbar(`${t("Incorrect username, password, or server address.")}`, {
            variant: "error",
          })
          if (!srcLocked)
            enqueueSnackbar(`${t("Are you sure you're logging into the right SensorBrain server?")}`, {
              variant: "info",
            })
        }
        setLoginClick(false)
        return
      }
      setLoginClick(false)
    }

    if (res?.authType === "participant") {
      await localStorage.setItem("lastTab" + res.identity.id, JSON.stringify(new Date().getTime()))
      await LAMP.SensorEvent.create(res.identity.id, {
        timestamp: Date.now(),
        sensor: "lamp.analytics",
        data: {
          type: "login",
          device_type: "Dashboard",
          user_agent: `LAMP-dashboard/${process.env.REACT_APP_GIT_SHA} ${window.navigator.userAgent}`,
        },
      } as any)
      await LAMP.Type.setAttachment(res.identity.id, "me", "lamp.participant.timezone", timezoneVal())
    }
    localStorage.setItem(
      "LAMP_user_" + res?.identity?.id,
      JSON.stringify({
        language: selectedLanguage,
      })
    )
    ;(async () => {
      await Service.deleteDB()
      await Service.deleteUserDB()
    })()
    // Successful login: forget earlier failed attempts so they don't count toward a lockout.
    localStorage.removeItem(LOGIN_ATTEMPTS_KEY)
    onComplete()
    setLoginClick(true)
    // .catch((err) => {
    //   // console.warn("error with auth request", err)
    //   enqueueSnackbar(`${t("Incorrect username, password, or server address.")}`, {
    //     variant: "error",
    //   })
    //   if (!srcLocked)
    //     enqueueSnackbar(`${t("Are you sure you're logging into the right SensorBrain server?")}`, { variant: "info" })
    //   setLoginClick(false)
    // })
  }

  const configuredProviders = LAMP.Auth._configuredProviders

  const handleOAuthLogin = async (socialProvider) => {
    const result = (await LAMP.Credential.startOAuth(socialProvider)) as any
    if (result.redirectUrl) {
      window.location.replace(result.redirectUrl)
    }
  }

  const timezoneVal = () => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    return timezone
  }

  return (
    <>
      <LoginFrame>
        <Grid item className={classes.loginInner}>
          {mfaRequest ? (
            <MfaCodeStep
              request={mfaRequest}
              onDone={(verified) => {
                setMfaRequest(null)
                mfaDone.current?.(verified)
                mfaDone.current = undefined
              }}
            />
          ) : (
            <>
              {(() => {
                // Show the friendly server card (logo + name) when the selected server
                // is a known deployment; fall back to the raw address for custom servers.
                // After a logout lamp-core forgets the server, but the sign-in still goes to the
                // saved one (lastServerSelected): show that one, not an empty box.
                const serverAddress = LAMP.Auth._auth.serverAddress || localStorage.getItem("lastServerSelected")
                const known = KNOWN_SERVERS.find((s) => s.apiServerUrl === serverAddress)
                return (
                  <Box
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      margin: "12px 0",
                      padding: "10px 16px",
                      borderRadius: 10,
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    <Box style={{ display: "flex", alignItems: "center" }}>
                      {known?.logo && (
                        <img
                          src={known.logo}
                          alt={known.name}
                          style={{ width: 28, height: 28, borderRadius: 6, marginRight: 10, objectFit: "contain" }}
                        />
                      )}
                      <span style={{ fontSize: 14, fontWeight: 600, color: "rgba(0,0,0,0.7)" }}>
                        {known?.name || serverAddress}
                      </span>
                    </Box>
                    {!srcLocked && (
                      <button
                        type="button"
                        onClick={() => clearServer()}
                        style={{
                          background: "transparent",
                          color: "#7599FF",
                          border: "1px solid #7599FF",
                          borderRadius: 8,
                          padding: "6px 20px",
                          fontSize: 13,
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        {`${t("Change")}`}
                      </button>
                    )}
                  </Box>
                )
              })()}
              {appOnlyParticipants && (
                <Box
                  role="alert"
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    margin: "4px 0 8px",
                    padding: "14px 16px",
                    borderRadius: 10,
                    background: "#fff7e6",
                    border: "1px solid #ffe2a8",
                    textAlign: "left",
                    wordBreak: "keep-all",
                  }}
                >
                  <Icon style={{ color: "#c77700", fontSize: 22, marginRight: 10 }}>smartphone</Icon>
                  <Box>
                    <Typography style={{ fontSize: 14, fontWeight: 700, color: "rgba(0,0,0,0.85)", marginBottom: 2 }}>
                      {`${t("The app is for participant accounts")}`}
                    </Typography>
                    <Typography style={{ fontSize: 13, lineHeight: 1.5, color: "rgba(0,0,0,0.6)" }}>
                      {`${t("Researchers and administrators, please sign in to the dashboard on a computer.")}`}
                    </Typography>
                  </Box>
                </Box>
              )}
              <form onSubmit={(e) => handleLogin(e)}>
                <Box>
                  {isLockedOut && (
                    <div style={{ marginBottom: "12px" }}>
                      <span style={{ color: "red", fontSize: "14px" }}>
                        {t("Too many login attempts. Try again after 1 hour.")}
                      </span>
                    </div>
                  )}
                  <TextField
                    select
                    label={`${t("Select Language")}`}
                    style={{ width: "100%" }}
                    onChange={(event) => {
                      setSelectedLanguage(event.target.value)
                    }}
                    variant="filled"
                    value={selectedLanguage || "en-US"}
                  >
                    {Object.keys(locale_lang).map((key, value) => {
                      if (userLanguages.includes(key)) {
                        return (
                          <MenuItem key={key} value={key}>
                            {locale_lang[key].native}
                          </MenuItem>
                        )
                      }
                    })}
                  </TextField>

                  <TextField
                    required
                    name="id"
                    type="email"
                    margin="normal"
                    variant="outlined"
                    style={{ width: "100%", height: 50 }}
                    placeholder={`${t("my.email@address.com")}`}
                    value={state.id || ""}
                    onChange={(event) => {
                      setAppOnlyParticipants(false)
                      handleChange(event)
                    }}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      classes: {
                        root: classes.textfieldStyle,
                      },
                      autoCapitalize: "off",
                    }}
                  />
                  <TextField
                    required
                    name="password"
                    type={showPassword ? "text" : "password"}
                    margin="normal"
                    variant="outlined"
                    style={{ width: "100%", height: 50, marginBottom: 40 }}
                    placeholder="•••••••••"
                    value={state.password || ""}
                    onChange={handleChange}
                    InputProps={{
                      classes: {
                        root: classes.textfieldStyle,
                      },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword(!showPassword)}
                            onMouseDown={(event) => event.preventDefault()}
                            edge="end"
                          >
                            <Icon>{showPassword ? "visibility_off" : "visibility"}</Icon>
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Box className={classes.buttonNav} width={1} textAlign="center">
                    <Fab
                      variant="extended"
                      type="submit"
                      style={{ background: "#7599FF", color: "White" }}
                      onClick={handleLogin}
                      className={loginClick && isLockedOut ? classes.loginDisabled : ""}
                      disabled={loginClick && isLockedOut}
                    >
                      {`${t("Login")}`}
                      <input
                        type="submit"
                        style={{
                          cursor: "pointer",
                          position: "absolute",
                          top: 0,
                          bottom: 0,
                          right: 0,
                          left: 0,
                          width: "100%",
                          opacity: 0,
                        }}
                        disabled={loginClick && isLockedOut}
                      />
                    </Fab>
                  </Box>
                </Box>
              </form>
              <Box>
                {configuredProviders.map((providerKey) => (
                  <Fab
                    variant="extended"
                    type="button"
                    style={{ background: "#7599FF", color: "White" }}
                    onClick={() => handleOAuthLogin(providerKey)}
                    key={`login-with-${providerKey}`}
                  >
                    {providerKey}
                  </Fab>
                ))}
              </Box>
            </>
          )}
        </Grid>
      </LoginFrame>
    </>
  )
}
